export const CHAT_CHART_JSON_MAX = 30_000;

export const CHAT_CHART_TYPES = [
  "line",
  "bar",
  "pie",
  "scatter",
  "radar",
  "sparkline",
  "gauge",
] as const;

export type ChatChartType = (typeof CHAT_CHART_TYPES)[number];

export type ChatChartSeriesData = {
  id?: string;
  label: string;
  data: (number | null)[];
  color?: string;
};

export type ChatChartSeriesPoints = {
  id?: string;
  label: string;
  points: {t: number; v: number | null}[];
  color?: string;
};

export type ChatPieSlice = {
  label: string;
  value: number;
  color?: string;
};

export type ChatScatterPoint = {
  x: number;
  y: number;
};

export type ChatScatterSeries = {
  id?: string;
  label: string;
  points: ChatScatterPoint[];
  color?: string;
};

export type ChatRadarSeries = {
  id?: string;
  label: string;
  data: number[];
  color?: string;
};

export type ChatCartesianChartSpec = {
  type: "line" | "bar";
  title?: string;
  xLabel?: string;
  yLabel?: string;
  categories?: string[];
  mode: "categories" | "time";
  categoriesAxis?: string[];
  timeAxis?: number[];
  series: ChatChartSeriesData[];
};

export type ChatPieChartSpec = {
  type: "pie";
  title?: string;
  series: ChatPieSlice[];
};

export type ChatScatterChartSpec = {
  type: "scatter";
  title?: string;
  xLabel?: string;
  yLabel?: string;
  series: ChatScatterSeries[];
};

export type ChatRadarChartSpec = {
  type: "radar";
  title?: string;
  categories: string[];
  series: ChatRadarSeries[];
};

export type ChatSparklineChartSpec = {
  type: "sparkline";
  title?: string;
  data: number[];
  plotType: "line" | "bar";
};

export type ChatGaugeChartSpec = {
  type: "gauge";
  title?: string;
  value: number | null;
  valueMin: number;
  valueMax: number;
};

export type ChatChartSpec =
  | ChatCartesianChartSpec
  | ChatPieChartSpec
  | ChatScatterChartSpec
  | ChatRadarChartSpec
  | ChatSparklineChartSpec
  | ChatGaugeChartSpec;

const CARTESIAN_ROOT_KEYS = new Set([
  "type",
  "title",
  "xLabel",
  "yLabel",
  "categories",
  "series",
]);
const PIE_ROOT_KEYS = new Set(["type", "title", "series"]);
const SCATTER_ROOT_KEYS = new Set(["type", "title", "xLabel", "yLabel", "series"]);
const RADAR_ROOT_KEYS = new Set(["type", "title", "categories", "series"]);
const SPARKLINE_ROOT_KEYS = new Set(["type", "title", "data", "plotType"]);
const GAUGE_ROOT_KEYS = new Set([
  "type",
  "title",
  "value",
  "valueMin",
  "valueMax",
]);

const SERIES_KEYS = new Set(["id", "label", "data", "points", "color"]);
const PIE_SLICE_KEYS = new Set(["label", "value", "color"]);
const SCATTER_POINT_KEYS = new Set(["x", "y"]);
const POINT_KEYS = new Set(["t", "v"]);
const RADAR_SERIES_KEYS = new Set(["id", "label", "data", "color"]);

const MAX_TITLE = 120;
const MAX_LABEL = 120;
const MAX_CATEGORIES = 200;
const MAX_SERIES = 8;
const MAX_POINTS_PER_SERIES = 500;
const MAX_CATEGORY_ITEM_LENGTH = 120;
const MAX_PIE_SLICES = 24;

const isPlainObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

const hasOnlyKeys = (obj: Record<string, unknown>, allowed: Set<string>) =>
  Object.keys(obj).every(key => allowed.has(key));

const isSafeText = (value: unknown, max: number): value is string =>
  typeof value === "string" &&
  value.length <= max &&
  !/[\u0000-\u001F\u007F<>]/.test(value);

const isSafeCssColor = (value: unknown): value is string => {
  if (typeof value !== "string") {
    return false;
  }
  const color = value.trim();
  if (color.length === 0 || color.length > 64) {
    return false;
  }
  if (/[<>"'`]|url\s*\(|expression\s*\(|javascript:/i.test(color)) {
    return false;
  }
  return (
    /^#([0-9a-fA-F]{3,8})$/.test(color) ||
    /^rgba?\([^)]*\)$/i.test(color) ||
    /^hsla?\([^)]*\)$/i.test(color) ||
    /^currentColor$/i.test(color) ||
    /^[a-zA-Z]{3,20}$/.test(color)
  );
};

const isChartValue = (value: unknown): value is number | null =>
  value === null ||
  (typeof value === "number" && Number.isFinite(value));

const isFiniteNumber = (value: unknown): value is number =>
  typeof value === "number" && Number.isFinite(value);

const parseCategories = (
  raw: unknown,
  required: boolean,
): string[] | null | undefined => {
  if (raw === undefined) {
    return required ? null : undefined;
  }
  if (!Array.isArray(raw)) {
    return null;
  }
  if (raw.length === 0 || raw.length > MAX_CATEGORIES) {
    return null;
  }
  if (
    !raw.every(
      item =>
        typeof item === "string" &&
        item.length <= MAX_CATEGORY_ITEM_LENGTH &&
        !/[\u0000-\u001F\u007F<>]/.test(item),
    )
  ) {
    return null;
  }
  return raw;
};

const parseOptionalTitle = (parsed: Record<string, unknown>) => {
  if (parsed.title !== undefined && !isSafeText(parsed.title, MAX_TITLE)) {
    return null;
  }
  return parsed.title as string | undefined;
};

const parseSeriesData = (raw: unknown): ChatChartSeriesData | null => {
  if (!isPlainObject(raw) || !hasOnlyKeys(raw, SERIES_KEYS)) {
    return null;
  }
  if (!isSafeText(raw.label, MAX_LABEL)) {
    return null;
  }
  if (raw.id !== undefined && !isSafeText(raw.id, MAX_LABEL)) {
    return null;
  }
  if (raw.color !== undefined && !isSafeCssColor(raw.color)) {
    return null;
  }
  if (!("data" in raw) || raw.points !== undefined) {
    return null;
  }
  if (!Array.isArray(raw.data)) {
    return null;
  }
  if (!raw.data.every(isChartValue)) {
    return null;
  }
  return {
    id: raw.id,
    label: raw.label,
    data: raw.data,
    color: raw.color,
  };
};

const parseSeriesPoints = (raw: unknown): ChatChartSeriesPoints | null => {
  if (!isPlainObject(raw) || !hasOnlyKeys(raw, SERIES_KEYS)) {
    return null;
  }
  if (!isSafeText(raw.label, MAX_LABEL)) {
    return null;
  }
  if (raw.id !== undefined && !isSafeText(raw.id, MAX_LABEL)) {
    return null;
  }
  if (raw.color !== undefined && !isSafeCssColor(raw.color)) {
    return null;
  }
  if (!("points" in raw) || raw.data !== undefined) {
    return null;
  }
  if (!Array.isArray(raw.points) || raw.points.length === 0) {
    return null;
  }
  if (raw.points.length > MAX_POINTS_PER_SERIES) {
    return null;
  }
  const points: {t: number; v: number | null}[] = [];
  for (const point of raw.points) {
    if (!isPlainObject(point) || !hasOnlyKeys(point, POINT_KEYS)) {
      return null;
    }
    if (typeof point.t !== "number" || !Number.isFinite(point.t)) {
      return null;
    }
    if (!isChartValue(point.v)) {
      return null;
    }
    points.push({t: point.t, v: point.v});
  }
  return {
    id: raw.id,
    label: raw.label,
    points,
    color: raw.color,
  };
};

const alignTimeSeries = (
  series: ChatChartSeriesPoints[],
): {timeAxis: number[]; series: ChatChartSeriesData[]} => {
  const timeSet = new Set<number>();
  for (const item of series) {
    for (const point of item.points) {
      timeSet.add(point.t);
    }
  }
  const timeAxis = Array.from(timeSet).sort((a, b) => a - b);
  const aligned = series.map(item => {
    const byTime = new Map(item.points.map(point => [point.t, point.v]));
    return {
      id: item.id,
      label: item.label,
      color: item.color,
      data: timeAxis.map(t => byTime.get(t) ?? null),
    };
  });
  return {timeAxis, series: aligned};
};

const parseCartesianChart = (
  parsed: Record<string, unknown>,
): ChatCartesianChartSpec | null => {
  if (parsed.type !== "line" && parsed.type !== "bar") {
    return null;
  }
  const title = parseOptionalTitle(parsed);
  if (title === null) {
    return null;
  }
  if (parsed.xLabel !== undefined && !isSafeText(parsed.xLabel, MAX_LABEL)) {
    return null;
  }
  if (parsed.yLabel !== undefined && !isSafeText(parsed.yLabel, MAX_LABEL)) {
    return null;
  }
  if (!Array.isArray(parsed.series) || parsed.series.length === 0) {
    return null;
  }
  if (parsed.series.length > MAX_SERIES) {
    return null;
  }

  const categoriesAxis = parseCategories(parsed.categories, false);
  if (categoriesAxis === null) {
    return null;
  }

  const parsedSeries: (ChatChartSeriesData | ChatChartSeriesPoints)[] = [];
  let mode: "categories" | "time" | null = null;

  for (const item of parsed.series) {
    const withData = parseSeriesData(item);
    const withPoints = withData ? null : parseSeriesPoints(item);
    const seriesItem = withData ?? withPoints;
    if (!seriesItem) {
      return null;
    }
    const itemMode = withData ? "categories" : "time";
    if (mode === null) {
      mode = itemMode;
    } else if (mode !== itemMode) {
      return null;
    }
    parsedSeries.push(seriesItem);
  }

  if (mode === "categories") {
    const dataSeries = parsedSeries as ChatChartSeriesData[];
    if (categoriesAxis) {
      for (const item of dataSeries) {
        if (item.data.length !== categoriesAxis.length) {
          return null;
        }
      }
    }
    const axis =
      categoriesAxis ??
      dataSeries[0].data.map((_value, index) => String(index + 1));
    return {
      type: parsed.type,
      title,
      xLabel: parsed.xLabel as string | undefined,
      yLabel: parsed.yLabel as string | undefined,
      categories: categoriesAxis,
      mode: "categories",
      categoriesAxis: axis,
      series: dataSeries,
    };
  }

  if (categoriesAxis) {
    return null;
  }

  const pointSeries = parsedSeries as ChatChartSeriesPoints[];
  const {timeAxis, series: aligned} = alignTimeSeries(pointSeries);
  if (timeAxis.length > MAX_POINTS_PER_SERIES * MAX_SERIES) {
    return null;
  }

  return {
    type: parsed.type,
    title,
    xLabel: parsed.xLabel as string | undefined,
    yLabel: parsed.yLabel as string | undefined,
    mode: "time",
    timeAxis,
    series: aligned,
  };
};

const parsePieSlice = (raw: unknown): ChatPieSlice | null => {
  if (!isPlainObject(raw) || !hasOnlyKeys(raw, PIE_SLICE_KEYS)) {
    return null;
  }
  if (!isSafeText(raw.label, MAX_LABEL)) {
    return null;
  }
  if (!isFiniteNumber(raw.value)) {
    return null;
  }
  if (raw.color !== undefined && !isSafeCssColor(raw.color)) {
    return null;
  }
  return {
    label: raw.label,
    value: raw.value,
    color: raw.color,
  };
};

const parsePieChart = (parsed: Record<string, unknown>): ChatPieChartSpec | null => {
  if (parsed.type !== "pie") {
    return null;
  }
  const title = parseOptionalTitle(parsed);
  if (title === null) {
    return null;
  }
  if (!Array.isArray(parsed.series) || parsed.series.length === 0) {
    return null;
  }
  if (parsed.series.length > MAX_PIE_SLICES) {
    return null;
  }
  const series: ChatPieSlice[] = [];
  for (const item of parsed.series) {
    const slice = parsePieSlice(item);
    if (!slice) {
      return null;
    }
    series.push(slice);
  }
  return {type: "pie", title, series};
};

const parseScatterPoint = (raw: unknown): ChatScatterPoint | null => {
  if (!isPlainObject(raw) || !hasOnlyKeys(raw, SCATTER_POINT_KEYS)) {
    return null;
  }
  if (!isFiniteNumber(raw.x) || !isFiniteNumber(raw.y)) {
    return null;
  }
  return {x: raw.x, y: raw.y};
};

const parseScatterSeries = (raw: unknown): ChatScatterSeries | null => {
  if (!isPlainObject(raw) || !hasOnlyKeys(raw, SERIES_KEYS)) {
    return null;
  }
  if (!isSafeText(raw.label, MAX_LABEL)) {
    return null;
  }
  if (raw.id !== undefined && !isSafeText(raw.id, MAX_LABEL)) {
    return null;
  }
  if (raw.color !== undefined && !isSafeCssColor(raw.color)) {
    return null;
  }
  if (!("points" in raw) || raw.data !== undefined) {
    return null;
  }
  if (!Array.isArray(raw.points) || raw.points.length === 0) {
    return null;
  }
  if (raw.points.length > MAX_POINTS_PER_SERIES) {
    return null;
  }
  const points: ChatScatterPoint[] = [];
  for (const point of raw.points) {
    const parsedPoint = parseScatterPoint(point);
    if (!parsedPoint) {
      return null;
    }
    points.push(parsedPoint);
  }
  return {
    id: raw.id,
    label: raw.label,
    points,
    color: raw.color,
  };
};

const parseScatterChart = (
  parsed: Record<string, unknown>,
): ChatScatterChartSpec | null => {
  if (parsed.type !== "scatter") {
    return null;
  }
  const title = parseOptionalTitle(parsed);
  if (title === null) {
    return null;
  }
  if (parsed.xLabel !== undefined && !isSafeText(parsed.xLabel, MAX_LABEL)) {
    return null;
  }
  if (parsed.yLabel !== undefined && !isSafeText(parsed.yLabel, MAX_LABEL)) {
    return null;
  }
  if (!Array.isArray(parsed.series) || parsed.series.length === 0) {
    return null;
  }
  if (parsed.series.length > MAX_SERIES) {
    return null;
  }
  const series: ChatScatterSeries[] = [];
  for (const item of parsed.series) {
    const parsedSeries = parseScatterSeries(item);
    if (!parsedSeries) {
      return null;
    }
    series.push(parsedSeries);
  }
  return {
    type: "scatter",
    title,
    xLabel: parsed.xLabel as string | undefined,
    yLabel: parsed.yLabel as string | undefined,
    series,
  };
};

const parseRadarSeries = (raw: unknown): ChatRadarSeries | null => {
  if (!isPlainObject(raw) || !hasOnlyKeys(raw, RADAR_SERIES_KEYS)) {
    return null;
  }
  if (!isSafeText(raw.label, MAX_LABEL)) {
    return null;
  }
  if (raw.id !== undefined && !isSafeText(raw.id, MAX_LABEL)) {
    return null;
  }
  if (raw.color !== undefined && !isSafeCssColor(raw.color)) {
    return null;
  }
  if (!Array.isArray(raw.data) || raw.data.length === 0) {
    return null;
  }
  if (raw.data.length > MAX_CATEGORIES) {
    return null;
  }
  if (!raw.data.every(isFiniteNumber)) {
    return null;
  }
  return {
    id: raw.id,
    label: raw.label,
    data: raw.data,
    color: raw.color,
  };
};

const parseRadarChart = (
  parsed: Record<string, unknown>,
): ChatRadarChartSpec | null => {
  if (parsed.type !== "radar") {
    return null;
  }
  const title = parseOptionalTitle(parsed);
  if (title === null) {
    return null;
  }
  const categories = parseCategories(parsed.categories, true);
  if (!categories) {
    return null;
  }
  if (!Array.isArray(parsed.series) || parsed.series.length === 0) {
    return null;
  }
  if (parsed.series.length > MAX_SERIES) {
    return null;
  }
  const series: ChatRadarSeries[] = [];
  for (const item of parsed.series) {
    const parsedSeries = parseRadarSeries(item);
    if (!parsedSeries) {
      return null;
    }
    if (parsedSeries.data.length !== categories.length) {
      return null;
    }
    series.push(parsedSeries);
  }
  return {type: "radar", title, categories, series};
};

const parseSparklineChart = (
  parsed: Record<string, unknown>,
): ChatSparklineChartSpec | null => {
  if (parsed.type !== "sparkline") {
    return null;
  }
  const title = parseOptionalTitle(parsed);
  if (title === null) {
    return null;
  }
  if (!Array.isArray(parsed.data) || parsed.data.length === 0) {
    return null;
  }
  if (parsed.data.length > MAX_POINTS_PER_SERIES) {
    return null;
  }
  if (!parsed.data.every(isFiniteNumber)) {
    return null;
  }
  const plotType =
    parsed.plotType === undefined
      ? "line"
      : parsed.plotType === "line" || parsed.plotType === "bar"
        ? parsed.plotType
        : null;
  if (plotType === null) {
    return null;
  }
  return {
    type: "sparkline",
    title,
    data: parsed.data,
    plotType,
  };
};

const parseGaugeChart = (
  parsed: Record<string, unknown>,
): ChatGaugeChartSpec | null => {
  if (parsed.type !== "gauge") {
    return null;
  }
  const title = parseOptionalTitle(parsed);
  if (title === null) {
    return null;
  }
  if (!("value" in parsed)) {
    return null;
  }
  if (parsed.value !== null && !isFiniteNumber(parsed.value)) {
    return null;
  }
  const valueMin =
    parsed.valueMin === undefined ? 0 : isFiniteNumber(parsed.valueMin) ? parsed.valueMin : null;
  const valueMax =
    parsed.valueMax === undefined
      ? 100
      : isFiniteNumber(parsed.valueMax)
        ? parsed.valueMax
        : null;
  if (valueMin === null || valueMax === null || valueMin >= valueMax) {
    return null;
  }
  return {
    type: "gauge",
    title,
    value: parsed.value as number | null,
    valueMin,
    valueMax,
  };
};

const rootKeysForType = (type: string): Set<string> | null => {
  switch (type) {
    case "line":
    case "bar":
      return CARTESIAN_ROOT_KEYS;
    case "pie":
      return PIE_ROOT_KEYS;
    case "scatter":
      return SCATTER_ROOT_KEYS;
    case "radar":
      return RADAR_ROOT_KEYS;
    case "sparkline":
      return SPARKLINE_ROOT_KEYS;
    case "gauge":
      return GAUGE_ROOT_KEYS;
    default:
      return null;
  }
};

export const parseChatChartSpec = (rawJson: string): ChatChartSpec | null => {
  if (rawJson.length > CHAT_CHART_JSON_MAX) {
    return null;
  }
  let parsed: unknown;
  try {
    parsed = JSON.parse(rawJson);
  } catch {
    return null;
  }
  if (!isPlainObject(parsed)) {
    return null;
  }
  if (
    typeof parsed.type !== "string" ||
    !CHAT_CHART_TYPES.includes(parsed.type as ChatChartType)
  ) {
    return null;
  }
  const allowedRoot = rootKeysForType(parsed.type);
  if (!allowedRoot || !hasOnlyKeys(parsed, allowedRoot)) {
    return null;
  }

  switch (parsed.type) {
    case "line":
    case "bar":
      return parseCartesianChart(parsed);
    case "pie":
      return parsePieChart(parsed);
    case "scatter":
      return parseScatterChart(parsed);
    case "radar":
      return parseRadarChart(parsed);
    case "sparkline":
      return parseSparklineChart(parsed);
    case "gauge":
      return parseGaugeChart(parsed);
    default:
      return null;
  }
};
